import { isEqual, pick } from 'lodash';
import { ViewConfiguration } from '../BalanceSheetTable/BalanceSheetTableContext';

/** Fields that suggestion presets can modify */
const SUGGESTION_FIELDS = ['showInEstate', 'separateInEstateDirectlyHeld', 'separateInEstateTrusts', 'showOutOfEstate', 'separateOutOfEstateTrusts'] as const;
type SuggestionConfig = Pick<ViewConfiguration, (typeof SUGGESTION_FIELDS)[number]>;
export interface BalanceSheetSuggestionPreset {
  id: string;
  label: string;
  /** The config values this suggestion applies */
  config: SuggestionConfig;
  /** Message shown after the suggestion is applied, describing what changed */
  responseMessage: string;
}

/** A suggestion that was applied by the user, shown in the chat history */
export interface AppliedSuggestion {
  preset: BalanceSheetSuggestionPreset;
}

/** The default (fully expanded) configuration */
const DEFAULT_CONFIG: SuggestionConfig = {
  showInEstate: true,
  separateInEstateDirectlyHeld: true,
  separateInEstateTrusts: true,
  showOutOfEstate: true,
  separateOutOfEstateTrusts: true
};
const SIMPLIFY_PRESET: BalanceSheetSuggestionPreset = {
  id: 'simplify',
  label: 'Simplify this balance sheet (consolidate trusts)',
  config: {
    showInEstate: true,
    separateInEstateDirectlyHeld: false,
    separateInEstateTrusts: false,
    showOutOfEstate: true,
    separateOutOfEstateTrusts: false
  },
  responseMessage: 'The balance sheet has been simplified by consolidating In Estate trusts into a single Held in Trust column.'
};
const EXPAND_IN_ESTATE_PRESET: BalanceSheetSuggestionPreset = {
  id: 'expand-in-estate',
  label: 'Expand only in estate trusts and holdings',
  config: {
    showInEstate: true,
    separateInEstateDirectlyHeld: true,
    separateInEstateTrusts: true,
    showOutOfEstate: true,
    separateOutOfEstateTrusts: false
  },
  responseMessage: 'The In Estate section has been expanded to show separate columns for directly held assets and trusts.'
};
const EXPAND_OUT_OF_ESTATE_PRESET: BalanceSheetSuggestionPreset = {
  id: 'expand-out-of-estate',
  label: 'Expand only out of estate trusts',
  config: {
    showInEstate: true,
    separateInEstateDirectlyHeld: false,
    separateInEstateTrusts: false,
    showOutOfEstate: true,
    separateOutOfEstateTrusts: true
  },
  responseMessage: 'The Out of Estate section has been expanded to show separate columns for each trust.'
};
const REVERT_PRESET: BalanceSheetSuggestionPreset = {
  id: 'revert',
  label: 'Revert to original configuration (separate all holdings and trusts)',
  config: DEFAULT_CONFIG,
  responseMessage: 'The balance sheet has been reverted to show all holdings and trusts in separate columns.'
};

/** All presets except revert (which is conditional) */
const STANDARD_PRESETS: BalanceSheetSuggestionPreset[] = [SIMPLIFY_PRESET, EXPAND_IN_ESTATE_PRESET, EXPAND_OUT_OF_ESTATE_PRESET];

/** Checks whether a preset's config matches the current config */
function presetMatchesCurrent(preset: BalanceSheetSuggestionPreset, currentConfig: ViewConfiguration): boolean {
  const currentRelevant = pick(currentConfig, SUGGESTION_FIELDS);
  return isEqual(preset.config, currentRelevant);
}

/**
 * Returns the list of available suggestions based on the current config state.
 *
 * - Standard presets are hidden when they match the current config
 * - "Revert to original" only shows after the user has modified config
 * - "Undo previous change" is handled separately (via hasHistory flag)
 */
export function getAvailableSuggestions(currentConfig: ViewConfiguration): BalanceSheetSuggestionPreset[] {
  const suggestions: BalanceSheetSuggestionPreset[] = [];
  for (const preset of STANDARD_PRESETS) {
    if (!presetMatchesCurrent(preset, currentConfig)) {
      suggestions.push(preset);
    }
  }

  // "Revert to original" appears whenever the current config differs
  // from the default — whether changed via suggestions, advanced
  // settings, or a prior session.
  if (!presetMatchesCurrent(REVERT_PRESET, currentConfig)) {
    suggestions.push(REVERT_PRESET);
  }
  return suggestions;
}